import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAbility } from '@casl/react';
import { Briefcase, Edit, FileDown, MapPin, Plus, Power, Search, User, Warehouse } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Can } from '../../components/Can';
import AccessRestricted from '../../components/AccessRestricted';
import { AbilityContext } from '../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import {
    Branch,
    BranchContact,
    Customer,
    ListStatusFilter,
    Project,
} from '../../types/lists.types';
import { branchesApi, customersApi, projectsApi } from '../../utils/lists-api';
import api from '../../utils/api';
import { cn } from '../../components/ui/utils';
import StatusPill from './components/StatusPill';
import ProjectFormDialog from './components/ProjectFormDialog';

const ALL_BRANCHES = 'all';

export default function ProjectsPage() {
    const ability = useAbility(AbilityContext);
    const canRead = ability.can(ACTIONS.READ, RESOURCES.LIST_PROJECT);

    const [projects, setProjects] = useState<Project[]>([]);
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [branches, setBranches] = useState<Branch[]>([]);
    const [users, setUsers] = useState<BranchContact[]>([]);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState<ListStatusFilter>('active');
    const [branchFilter, setBranchFilter] = useState<string>(ALL_BRANCHES);
    const [search, setSearch] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState<Project | null>(null);

    const fetchProjects = useCallback(async () => {
        try {
            setLoading(true);
            setProjects(await projectsApi.list({
                status,
                branchId: branchFilter === ALL_BRANCHES ? undefined : branchFilter,
            }));
        } catch (err: any) {
            toast.error(err?.message || 'Failed to load projects');
        } finally {
            setLoading(false);
        }
    }, [status, branchFilter]);

    useEffect(() => {
        if (canRead) fetchProjects();
    }, [canRead, fetchProjects]);

    // Selector options: Active customers and branches only, but every user.
    useEffect(() => {
        if (!canRead) return;
        customersApi.list({ status: 'active' }).then(setCustomers).catch(() => undefined);
        branchesApi.list({ status: 'active' }).then(setBranches).catch(() => undefined);
        api.get<{ success: boolean; users: BranchContact[] }>('/api/users?all_users=true')
            .then((res) => setUsers(Array.isArray(res?.users) ? res.users : []))
            .catch(() => toast.error('Failed to load people list'));
    }, [canRead]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return projects;
        return projects.filter((p) =>
            [p.projectName, p.projectNumber, p.siteAddress, p.customer?.tradingName, p.projectManager?.name]
                .filter(Boolean)
                .some((v) => (v as string).toLowerCase().includes(q))
        );
    }, [projects, search]);

    const handleToggleStatus = async (project: Project) => {
        try {
            await projectsApi.setStatus(project.id, !project.isActive);
            toast.success(`${project.projectName} set to ${project.isActive ? 'Inactive' : 'Active'}`);
            fetchProjects();
        } catch (err: any) {
            toast.error(err?.message || 'Failed to update status');
        }
    };

    const handleExport = async (format: 'csv' | 'excel') => {
        try {
            await projectsApi.export(format);
            toast.success(`Exported as ${format.toUpperCase()}`);
        } catch {
            toast.error('Failed to generate export file');
        }
    };

    const openAdd = () => { setEditing(null); setDialogOpen(true); };
    const openEdit = (project: Project) => { setEditing(project); setDialogOpen(true); };

    if (!canRead) return <AccessRestricted />;

    return (
        <div className="space-y-4 md:space-y-6 p-2 md:p-0">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold">Projects</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Projects are assigned to a Branch and a Customer.
                    </p>
                </div>
                <div className="flex flex-col md:flex-row items-center gap-2 w-full md:w-auto">
                    <Can I={ACTIONS.EXPORT} a={RESOURCES.LIST_PROJECT}>
                        <Button
                            variant="outline"
                            onClick={() => handleExport('csv')}
                            className="flex items-center gap-2 w-full md:w-auto border-slate-200 hover:bg-slate-50"
                        >
                            <FileDown size={16} /><span>Export CSV</span>
                        </Button>
                    </Can>
                    <Can I={ACTIONS.CREATE} a={RESOURCES.LIST_PROJECT}>
                        <Button
                            onClick={openAdd}
                            className="flex items-center gap-2 w-full md:w-auto bg-slate-900 hover:bg-slate-800 text-white border-0"
                        >
                            <Plus size={16} /><span>Add Project</span>
                        </Button>
                    </Can>
                </div>
            </div>

            {/* Active / Inactive tabs */}
            <Tabs value={status} onValueChange={(v) => setStatus(v as ListStatusFilter)} className="w-full">
                <TabsList className="bg-gray-100 p-1 rounded-full w-full md:w-fit">
                    <TabsTrigger value="active" className="rounded-full flex-1 md:flex-none px-4 py-2 data-[state=active]:bg-white">
                        Active
                    </TabsTrigger>
                    <TabsTrigger value="inactive" className="rounded-full flex-1 md:flex-none px-4 py-2 data-[state=active]:bg-white">
                        Inactive
                    </TabsTrigger>
                </TabsList>
            </Tabs>

            {/* Search + branch filter */}
            <div className="flex flex-col md:flex-row items-start md:items-center w-full gap-4 flex-wrap">
                <div className="relative w-full md:flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search projects..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10 w-full"
                    />
                </div>
                <div className="w-full md:w-[250px] min-w-0">
                    <Select value={branchFilter} onValueChange={setBranchFilter}>
                        <SelectTrigger className="w-full bg-white truncate overflow-hidden">
                            <SelectValue placeholder="Filter by Branch" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={ALL_BRANCHES}>All Branches</SelectItem>
                            {branches.map((b) => (
                                <SelectItem key={b.id} value={String(b.id)}>{b.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <span className="text-sm text-muted-foreground">{filtered.length} records</span>
            </div>

            {loading ? (
                <div className="space-y-3 animate-pulse">
                    {[0, 1, 2, 3].map((i) => <div key={i} className="h-14 bg-gray-300 rounded-md" />)}
                </div>
            ) : filtered.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">No projects found</div>
            ) : (
                <>
                    {/* Mobile cards */}
                    <div className="md:hidden space-y-3">
                        {filtered.map((p) => (
                            <Card key={p.id} className="p-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <Briefcase size={16} className="text-muted-foreground shrink-0" />
                                            <span className="font-medium truncate">{p.projectName}</span>
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-1">#{p.projectNumber}</p>
                                    </div>
                                    <StatusPill isActive={p.isActive} />
                                </div>
                                <div className="mt-3 space-y-1 text-sm text-muted-foreground">
                                    <p>{p.siteAddress}</p>
                                    <p>{p.customer?.tradingName ?? '–'}</p>
                                    <p className="flex items-center gap-1.5"><User size={14} />{p.projectManager?.name ?? '–'}</p>
                                    <p className="flex items-center gap-1.5"><MapPin size={14} />{p.branch?.name ?? '–'}</p>
                                    <p className="flex items-center gap-1.5">
                                        <Warehouse size={14} />
                                        {p.yard ? `${p.yard.yardName} (${p.yard.yardCode})` : '–'}
                                    </p>
                                </div>
                                <div className="flex justify-end gap-2 mt-3">
                                    <Can I={ACTIONS.UPDATE} a={RESOURCES.LIST_PROJECT}>
                                        <Button variant="outline" size="icon" onClick={() => openEdit(p)} aria-label="Edit">
                                            <Edit size={16} />
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            onClick={() => handleToggleStatus(p)}
                                            aria-label={p.isActive ? 'Set inactive' : 'Set active'}
                                            className={cn(p.isActive && 'text-destructive')}
                                        >
                                            <Power size={16} />
                                        </Button>
                                    </Can>
                                </div>
                            </Card>
                        ))}
                    </div>

                    {/* Desktop table */}
                    <Card className="hidden md:block">
                        <CardContent className="p-3">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="font-semibold text-slate-900">Project Name</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Number</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Site Address</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Customer</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Project Manager</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Branch</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Yard</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Status</TableHead>
                                        <TableHead className="font-semibold text-slate-900 text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filtered.map((p) => (
                                        <TableRow key={p.id}>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <Briefcase size={16} className="text-muted-foreground" />
                                                    {p.projectName}
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-muted-foreground">{p.projectNumber}</TableCell>
                                            <TableCell className="whitespace-normal max-w-[240px]">{p.siteAddress}</TableCell>
                                            <TableCell>{p.customer?.tradingName ?? <span className="text-muted-foreground">–</span>}</TableCell>
                                            <TableCell>{p.projectManager?.name ?? <span className="text-muted-foreground">–</span>}</TableCell>
                                            <TableCell>{p.branch?.name ?? <span className="text-muted-foreground">–</span>}</TableCell>
                                            <TableCell>
                                                {p.yard
                                                    ? <span className="font-mono text-sm">{p.yard.yardCode}</span>
                                                    : <span className="text-muted-foreground">–</span>}
                                            </TableCell>
                                            <TableCell><StatusPill isActive={p.isActive} /></TableCell>
                                            <TableCell>
                                                <div className="flex justify-end gap-2">
                                                    <Can I={ACTIONS.UPDATE} a={RESOURCES.LIST_PROJECT}>
                                                        <Button variant="outline" size="icon" onClick={() => openEdit(p)} aria-label="Edit">
                                                            <Edit size={16} />
                                                        </Button>
                                                        <Button
                                                            variant="outline"
                                                            size="icon"
                                                            onClick={() => handleToggleStatus(p)}
                                                            aria-label={p.isActive ? 'Set inactive' : 'Set active'}
                                                            className={cn(p.isActive && 'text-destructive')}
                                                        >
                                                            <Power size={16} />
                                                        </Button>
                                                    </Can>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </>
            )}

            <ProjectFormDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                project={editing}
                customers={customers}
                users={users}
                branches={branches}
                onSaved={fetchProjects}
            />
        </div>
    );
}
